import React, { forwardRef, useMemo } from 'react'
import { ClickAwayListenerProps } from '@mui/base/ClickAwayListener/ClickAwayListener'
import { ClickAwayListener, Paper, Popper, PopperProps } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { State } from '@popperjs/core/lib/types'
import classNames from 'classnames'
import * as R from 'ramda'

import { ClassesType } from '../types'
import { isTooltip } from '../utils'
import PuiCloseButton from './buttons/PuiCloseButton'

const useStyles = makeStyles(
  theme => ({
    popper: {
      zIndex: theme.zIndex.modal + 1,
      boxShadow: theme.constants.blockShadow,
    },
    paper: {},
  }),
  { name: 'PuiPopper' },
)

const isNotEmpty = R.complement(R.isEmpty)

export interface PuiPopperProps extends Omit<PopperProps, 'placement'> {
  readonly classes?: ClassesType<typeof useStyles>
  readonly closeOnTooltipsEvents?: boolean
  readonly disableClickAwayListener?: boolean
  readonly eventName?: ClickAwayListenerProps['mouseEvent']
  readonly hideCloseButton?: boolean
  readonly onClose: (event: any) => void
  readonly placement?: PopperProps['placement'] | 'center'
}

const PuiPopper = forwardRef<HTMLDivElement, PuiPopperProps>(function PuiPopper(
  {
    disablePortal,
    disableClickAwayListener = false,
    hideCloseButton,
    modifiers: modifiersProp,
    anchorEl,
    children,
    onClose,
    className,
    classes: classesProp,
    eventName = 'onMouseDown',
    placement = 'center',
    closeOnTooltipsEvents,
    ...rest
  },
  ref,
) {
  const classes = useStyles({ classes: classesProp })

  const onPopperClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
  }

  const isCentered = placement === 'center'

  const paper = (
    <Paper className={classes.paper}>
      <>
        {!hideCloseButton && <PuiCloseButton onClick={onClose} />}
        {children}
      </>
    </Paper>
  )

  const computeStyles = ({ state }: { state: State }) => {
    state.styles.popper = {
      ...state.styles.popper,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    }

    return state
  }

  const ownModifiers: PopperProps['modifiers'] = [
    {
      name: 'flip',
      enabled: true,
    },
    {
      name: 'preventOverflow',
      enabled: true,
    },
    isCentered
      ? {
        name: 'computeStyles',
        enabled: true,
        fn: computeStyles,
      }
      : {},
  ].filter(isNotEmpty)

  const modifiers: PopperProps['modifiers'] = useMemo(
    () =>
      R.uniqBy(R.propOr('modifier', 'name'), [
        ...(modifiersProp || []),
        ...ownModifiers,
      ]),
    [modifiersProp],
  )

  return (
    <Popper
      anchorEl={anchorEl}
      className={classNames(className, classes.popper)}
      disablePortal={disablePortal ?? true}
      modifiers={modifiers}
      placement={isCentered ? undefined : placement}
      ref={ref}
      onClick={onPopperClick}
      {...rest}
    >
      {disableClickAwayListener ? (
        paper
      ) : (
        <ClickAwayListener
          mouseEvent={eventName}
          touchEvent="onTouchStart"
          onClickAway={event => {
            // ToDo: consider better solution, to render tooltips not in portal, but near element itself
            if (
              closeOnTooltipsEvents ||
              !isTooltip(event.target as HTMLElement)
            ) {
              onClose(event)
            }
          }}
        >
          {paper}
        </ClickAwayListener>
      )}
    </Popper>
  )
})

export default PuiPopper
